import React, { Fragment, useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbUpIcon from '@atlaskit/icon/core/thumbs-up';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics/hooks/use-sain-usage';
import { onDislikeButtonClick, onLikeButtonClick } from '../analytics';

import { messages } from './messages';
import { FeedbackButtonIconWrapper, Rotate180IconWrapper } from './styled';

type FeedbackButtonProps = {
	active: boolean;
	onClick: (e: any) => void;
	tooltipContent: string;
	icon: JSX.Element;
	isDisabled?: boolean;
	testId?: string;
};

const FeedbackButton = ({
	active,
	isDisabled,
	onClick: onClickHandler,
	tooltipContent,
	testId,
	icon,
}: FeedbackButtonProps) => {
	return (
		<Tooltip position={'top'} content={tooltipContent}>
			{({ onClick: _onClick, ...tooltipProps }) => (
				<Button
					aria-label={tooltipContent}
					spacing={'none'}
					appearance={isDisabled ? 'subtle-link' : 'subtle'}
					iconBefore={
						<FeedbackButtonIconWrapper
							active={active}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ cursor: 'pointer' }}
							{...(testId && { 'data-testid': testId })}
							isDisabled={isDisabled || false}
						>
							{icon}
						</FeedbackButtonIconWrapper>
					}
					{...(!isDisabled && { onClick: onClickHandler })}
					{...tooltipProps}
				/>
			)}
		</Tooltip>
	);
};

type FeedbackButtonsProps = {
	setShowFeedbackOptionsModal: (value: boolean) => void;
	setDisplayFlag: (value: boolean) => void;
};

export const FeedbackButtons = ({
	setShowFeedbackOptionsModal,
	setDisplayFlag,
}: FeedbackButtonsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		commonAttributes,
		fireAnalyticsEvent,
		updateContextCache,
		liked,
		setLiked,
		likesDisabled,
		setLikesDisabled,
	} = useAIAnswerContext();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const { serveExperiment: serveDefinitionsUXExp } = getFrontendExperiment({
		abTestId: 'kd_definitions_ux_uplift_exp',
	});
	const serveDefinitionUXUpdates = serveDefinitionsUXExp && fg('kd_definitions_ux_uplift_exp_gate');

	// Using a single state to represent both like/dislike states
	// true - liked, false - disliked, undefined - neither are selected
	const likeButtonCallback = useCallback(
		(e: any) => {
			e.preventDefault();
			setDisplayFlag(true);
			setLiked(true);
			setLikesDisabled(true);
			updateContextCache({ collapsed: false, liked: true });
			fireAnalyticsEvent(onLikeButtonClick(commonAttributes));
			fireSAINUsageEvent({
				metCriteria: SainAction.POSITIVE_FEEDBACK_PROVIDED,
				commonAttributes,
			});
		},
		[
			setLiked,
			setLikesDisabled,
			fireAnalyticsEvent,
			commonAttributes,
			updateContextCache,
			setDisplayFlag,
			fireSAINUsageEvent,
		],
	);

	const dislikeButtonCallback = useCallback(
		(e: any) => {
			e.preventDefault();
			setShowFeedbackOptionsModal(true);
			setLiked(false);
			setLikesDisabled(true);
			updateContextCache({ collapsed: false, liked: false });
			fireAnalyticsEvent(onDislikeButtonClick(commonAttributes));
			fireSAINUsageEvent({
				metCriteria: SainAction.NEGATIVE_FEEDBACK_PROVIDED,
				commonAttributes,
			});
		},
		[
			setLiked,
			setLikesDisabled,
			fireAnalyticsEvent,
			commonAttributes,
			updateContextCache,
			setShowFeedbackOptionsModal,
			fireSAINUsageEvent,
		],
	);

	return (
		<Fragment>
			<FeedbackButton
				active={liked === true}
				onClick={likeButtonCallback}
				tooltipContent={formatMessage(messages.definition_footer_like_button_tooltip)}
				icon={
					<ThumbUpIcon
						label=""
						spacing="spacious"
						color={token('color.icon')}
						size={serveDefinitionUXUpdates ? 'small' : 'medium'}
					/>
				}
				isDisabled={liked && likesDisabled}
				testId="definition-footer-like-button"
			/>
			<FeedbackButton
				active={liked === false}
				onClick={dislikeButtonCallback}
				tooltipContent={formatMessage(messages.definition_footer_dislike_button_tooltip)}
				icon={
					serveDefinitionUXUpdates ? (
						<ThumbsDownIcon label="" spacing="spacious" size="small" color={token('color.icon')} />
					) : (
						<Rotate180IconWrapper>
							<ThumbUpIcon label="" spacing="spacious" color={token('color.icon')} />
						</Rotate180IconWrapper>
					)
				}
				isDisabled={!liked && likesDisabled}
				testId="definition-footer-dislike-button"
			/>
		</Fragment>
	);
};
